import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {allWords, titleCase} from '../../lib/urlTools';
import {commaFormatNumber, percentageFormatting, thousandsSeparatorsFormatting} from '../../lib/formatting';
import moment from 'moment';
import {isEmpty} from 'lodash';
import _cloneDeep from 'lodash/cloneDeep';
import SuburbPropertyCounts from './SuburbPropertyCounts';
import styles from './SuburbBlurb.scss';

const SuburbBlurb = ({ suburb, marketTrends, onSuburbProfile = false, onPdp= false, onSrp = false, propertyStatus = '' }) => {
  const { currentOffMarketCount, currentRentalListingCount, currentSaleListingCount, currentRecentSalesCountByHouse, currentRecentSalesCountByUnit } = suburb.suburb_detail;
  const totalOffmarketCount = currentOffMarketCount ? currentOffMarketCount : 0;
  const totalProperties = parseInt(totalOffmarketCount) + parseInt(currentRentalListingCount) + parseInt(currentSaleListingCount);
  const suburbText = suburb.suburb_detail.address.suburb.replace(allWords, titleCase);

  const getSeriesDataList = (propertyType, metricId) => {
    let seriesDataList = [];

    if(marketTrends.status === 'success' ){
      const metricTrendsData = _cloneDeep(marketTrends);
      if(metricTrendsData?.metrics[propertyType]){
        const dataList = metricTrendsData.metrics[propertyType][10];
        if(dataList?.length > 0) {
          const medianMetric = dataList.filter(d => d.metricTypeId === metricId);
          if(medianMetric?.length > 0) {
            seriesDataList = medianMetric[0].seriesDataList;
          }
        }
      }
    }
    return seriesDataList;
  };


  const getMedianValueChange = (propertyType, metricType) => {
    let medianValueText = '';
    const seriesDataList = getSeriesDataList(propertyType, metricType);
    const medianListLength = seriesDataList.length;

    if(medianListLength > 0) {
      const medianValue = seriesDataList[medianListLength - 1].value;
      const dataPercentage = percentageFormatting(medianValue);
      if(medianValue !== 0) {
        medianValueText = `have seen a ${dataPercentage} ${medianValue > 0 ? 'increase' : 'decrease'}`;
      }
    }

    return medianValueText;
  };

  const getMedianValueChangeText = (duration, metricType) => {
    const houseText = getMedianValueChange('House', metricType);
    const unitText = getMedianValueChange('Unit', metricType);
    let medianValue5YearText = '';

    if(houseText || unitText) {
      medianValue5YearText = ` Over the last ${duration},`;

      if(houseText) {
        medianValue5YearText += ` Houses in ${suburbText} ${houseText} in median value${unitText ? ', while Units ' + unitText : ''}. `;
      } else if(houseText === '' && unitText) {
        medianValue5YearText += ` Units in ${suburbText} ${unitText} in median value. `;
      }
    }

    return medianValue5YearText;
  };

  const getLatestMedianData = (propertyType, metricType) => {
    let medianData = {};

    const seriesDataList = getSeriesDataList(propertyType, metricType);
    const seriesDataListLength = seriesDataList.length;

    if(seriesDataListLength > 0) {
      medianData = seriesDataList[seriesDataListLength - 1];
      medianData.value = '$' + thousandsSeparatorsFormatting(medianData.value.toFixed(0));
    }

    return medianData;
  };

  const getLatestMedianValueText = () => {
    const houseData = getLatestMedianData('House', 11);
    const unitData = getLatestMedianData('Unit', 11);

    let valueText = '';

    if(!isEmpty(houseData) || !isEmpty(unitData)) {
      valueText += 'The median value for';
    }

    if(!isEmpty(houseData)) {
      valueText += ` Houses in ${suburbText} is ${houseData.value}${!isEmpty(unitData) ? ' while the median value for Units is ' + unitData.value : ''}.`;
    } else if(isEmpty(houseData) && !isEmpty(unitData)) {
      valueText += ` Units in ${suburbText} is ${unitData.value}.`;
    }

    return valueText ? <li>{valueText}</li> : '';
  };

  const getLatestMedianRentText = (isInsideList = false) => {
    const houseData = getLatestMedianData('House', 49);
    const unitData = getLatestMedianData('Unit', 49);

    let valueText = '';

    if(!isEmpty(houseData)) {
      valueText += `Houses have a median rent of ${houseData.value}${!isEmpty(unitData) ? ' while Units have a median rent of ' + unitData.value : ''}`;
    } else if(isEmpty(houseData) && !isEmpty(unitData)) {
      valueText += `Units have a median rent of ${unitData.value}`;
    }

    if(isInsideList && valueText) return <li>{valueText + '.'}</li>;

    return valueText;
  };


  const getLastUpdateDate = (closingCharacter = '') => {
    const houseData = getLatestMedianData('House', 11);
    const unitData = getLatestMedianData('Unit', 11);
    let lastUpdateText = '';

    if(!isEmpty(houseData) || !isEmpty(unitData)) {
      lastUpdateText = ' As at ';
    }
    if(!isEmpty(houseData)) {
      lastUpdateText += moment(houseData.dateTime).format('DD MMMM YYYY');
    } else if(isEmpty(houseData) && !isEmpty(unitData)) {
      lastUpdateText += moment(unitData.dateTime).format('DD MMMM YYYY');
    }

    if(lastUpdateText) { lastUpdateText += closingCharacter; }

    return lastUpdateText;
  };

  const removeLastParagraphOnDescription = () => {
    const description = _cloneDeep(suburb?.suburb_detail?.description);
    if (description) {
      const descriptionParagraphs = description.split('. ');
      if(descriptionParagraphs[descriptionParagraphs.length - 1].includes('Currently the median sales price')) {
        descriptionParagraphs.pop();
      }
      const suburbDescription = descriptionParagraphs.join('. ');
      return suburbDescription.slice(-1) === '.' ? suburbDescription : suburbDescription + '.';
    }
    return '';
  };

  const suburbDescription = removeLastParagraphOnDescription();

  const getLatestMedianValueTextForSRP = () => {
    const houseData = getLatestMedianData('House', 21);
    const unitData = getLatestMedianData('Unit', 21);

    let valueText = '';
    if(!isEmpty(houseData) || !isEmpty(unitData)) {
      valueText += `In ${suburbText}, the `;
    }

    if(!isEmpty(houseData)) {
      valueText += `median house sale price is ${houseData.value} based on ${currentRecentSalesCountByHouse} houses sold in the past 12 months. 
      ${!isEmpty(unitData) ? ` The median unit sale price is ${unitData.value} based on ${currentRecentSalesCountByUnit} units sold in the past 12 months` : ''}.`;
    } else if(isEmpty(houseData) && !isEmpty(unitData)) {
      valueText += `median unit sale price is ${unitData.value} based on ${currentRecentSalesCountByUnit} units sold in the past 12 months. `;
    }

    return valueText;
  };

  const getMedianRentChange = (propertyType, metricType) => {
    let medianValueText = '';
    const seriesDataList = getSeriesDataList(propertyType, metricType);
    const medianListLength = seriesDataList.length;

    if(medianListLength > 0) {
      const medianValue = seriesDataList[medianListLength - 1].value;
      const dataPercentage = percentageFormatting(Math.abs(medianValue));

      medianValueText = `, representing a ${dataPercentage} ${medianValue > 0 ? 'increase' : 'decrease'} over the last 12 months.`;
    }
    return medianValueText;
  };

  const renderPropertyDescription = () =>
    <p>
      {
        (suburbDescription) ?
          suburbDescription :
          suburb.suburb_detail.address.suburb.replace(allWords, titleCase)
          + ' is a suburb in '
          + suburb.suburb_detail.address.stateCode
          + ', Australia.'
      }
    </p>;

  const renderOnSuburbProfileAndPDP = () => onSuburbProfile || onPdp ?
    <div className={`${onPdp ? styles.pdpWidget : 'container mb-5'}`}>
      <div className="lead">
        <a id="Introduction"/>
        { (onPdp && !onSuburbProfile) && renderPropertyDescription() }
        <p>
          {`${suburbText} has ${commaFormatNumber(totalProperties)} properties.${getMedianValueChangeText('5 years', 90)}`}
          {getLastUpdateDate(':')}
        </p>
        <ul>
          {getLatestMedianValueText()}
          {getLatestMedianRentText(true)}
        </ul>
        <SuburbPropertyCounts/><br/>
        { (!onPdp && onSuburbProfile) && renderPropertyDescription() }
      </div>
    </div> : null;


  const renderOnSRP = () => onSrp ?
    <div className={styles.srpWidget}>
      {(propertyStatus === '' || propertyStatus === 'off market') &&
        <React.Fragment>
          <p>
            {`${suburbText} has ${commaFormatNumber(totalProperties)} properties.${getMedianValueChangeText('5 years', 90)}`}
            {getLastUpdateDate(':')}
          </p>
          <ul>
            {getLatestMedianValueText()}
            {getLatestMedianRentText(true)}
          </ul>
          <SuburbPropertyCounts showLearnMoreLink={true}/>
        </React.Fragment>
      }

      {(propertyStatus === 'for sale' || propertyStatus === 'sold') &&
        <p>
          { getLatestMedianValueTextForSRP() }
          { getMedianValueChangeText('12 months', 88) }
          <SuburbPropertyCounts isForSRP={true} isForSaleAndSold={true} showLearnMoreLink={true}/>
        </p>
      }

      {(propertyStatus === 'for rent' ) &&
        <p>
          {getLastUpdateDate(` in ${suburbText}, `)}
          {getLatestMedianRentText()}
          {`${getMedianRentChange('Unit', 47)} `}
          <SuburbPropertyCounts isForSRP={true} isForRent={true} showLearnMoreLink={true}/>
        </p>
      }
    </div> : null;

  return (
    <React.Fragment>
      { renderOnSuburbProfileAndPDP() }
      { renderOnSRP() }
    </React.Fragment>
  );
};

SuburbBlurb.propTypes = {
  suburb: PropTypes.object.isRequired,
  marketTrends: PropTypes.object.isRequired,
  onSuburbProfile: PropTypes.bool,
  onPdp: PropTypes.bool,
  onSrp: PropTypes.bool,
  propertyStatus: PropTypes.string
};

const mapStateToProps = (state) => ({suburb: state.suburb, marketTrends: state.marketTrends});
const mapDispatchToProps = {};
export default connect(mapStateToProps, mapDispatchToProps)(SuburbBlurb);
